/**
 * First Loop — canonical mapping from `GET /api/home/current_mission`
 * to the `activeMission` shape consumed by `CurrentMissionCard`.
 *
 * Pure function, no side effects. Returns `null` when the backend
 * reports no available mission (first-time users unchanged).
 *
 * Regression lock: every field the backend supplies for the loop
 * presentation (XX.276 fields + First Loop `last_result` /
 * `pointed_question`) must pass through here. Dropping a field
 * silently disables its card surface (this was the XX.276 wiring
 * gap: Chat.jsx copied only 5 fields and the urgency banner /
 * problem headline / next-test block were unreachable).
 *
 * Never fabricates: absent fields stay `null`; the card renders
 * each surface only when its field is present.
 *
 * @param {object} data — raw `GET /api/home/current_mission` payload
 * @returns {object|null} — `activeMission` for `CurrentMissionCard`
 */
export function buildActiveMission(data) {
  if (!data || !data.available || !data.mission_id || !data.focus_text) {
    return null;
  }
  return {
    mission_id: data.mission_id,
    focus_text: data.focus_text,
    focus_area: data.focus_area || null,
    focus_state: data.focus_state || "active",
    source: data.source || null,
    // Iter XX.276 — loop-presentation fields (previously dropped here).
    xx276_v1: data.xx276_v1 === true,
    problem: data.problem || null,
    urgency_label: data.urgency_label || null,
    next_moment: data.next_moment || null,
    days_until_event:
      typeof data.days_until_event === "number" ? data.days_until_event : null,
    // First Loop — verbatim last result (only when history exists;
    // never fabricated here) + backend-generated pointed question
    // (frontend never invents one).
    last_result: data.last_result || null,
    pointed_question: data.pointed_question || null,
  };
}
